import {
  NumberInput,
  Select,
  TextInput,
  Text,
  Modal,
  UnstyledButton,
  Alert,
  ScrollArea,
  rem,
  Button,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { HiPencil } from 'react-icons/hi2';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { DatePicker, type DatePickerProps } from '@mantine/dates';
import { IconInfoCircle, IconPlus, IconClock } from '@tabler/icons-react';
import { TbTrashXFilled } from 'react-icons/tb';
import { LuCopyPlus } from 'react-icons/lu';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import useAvailabilities from '@/hooks/employee/useAvailabilities';
import dayjs from 'dayjs';
import { dateFormat } from '@/index';
import useCreateAvailability from '@/hooks/employee/useCreateAvailability';
import {
  createAvailabilitySchema,
  type CreateAvailability,
} from '@/schemas/API/POST/employee/availability/request';
import useDeleteAvailability from '@/hooks/employee/useDeleteAvailability';
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';
import useUpdateAvailabilities from '@/hooks/employee/useUpdateAvailabilities';
import { generateAvailabilities } from '@/utils/availabilityUtils';
import { goodNotification } from '@/components/Notification/Notification';
import { displayError } from '@/utils/error/errorHandler';
import useEmployeeSchedule from '@/hooks/employee/useEmployeeSchedule';
import TimeInput from '@/components/Input/TimeInput/TimeInput';
import { useAvailabilityRefresh } from '../../EmployeeAvailabilitySection';

export default function AvailabilitySettingsCard() {
  const { availabilities, error } = useAvailabilities();
  const { createAvailability } = useCreateAvailability();
  const { deleteAvailability } = useDeleteAvailability();
  const { updateAvailabilities } = useUpdateAvailabilities();
  const { mutate } = useEmployeeSchedule();
  const { refresh } = useAvailabilityRefresh();

  const createForm = useForm<CreateAvailability>({
    initialValues: {
      date: '',
      startTime: '',
      endTime: '',
    },
    validate: zodResolver(createAvailabilitySchema),
  });

  const repeatFormSchema = z.object({
    count: z.number().int().min(1).max(5),
    freq: z.enum(['dnů', 'týdnů', 'ob. týdnů', 'měsíců']),
  });
  const repeatForm = useForm<z.infer<typeof repeatFormSchema>>({
    initialValues: {
      count: 1,
      freq: 'týdnů',
    },
    validate: zodResolver(repeatFormSchema),
  });

  // Date that user selects in calendar
  const [curDate, setCurDate] = useState<Date | null>(null);
  const [isCreateError, setIsCreateError] = useState<boolean>(false);
  const [isRepeatError, setIsRepeatError] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(null);
  const [isShowOpen, setIsShowOpen] = useState<boolean>(true);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const [opened, { open, close }] = useDisclosure(false);

  // Reset values in add form when curDate changes (user selects new day)
  useEffect(() => {
    createForm.reset();
    if (curDate)
      createForm.setFieldValue('date', dayjs(curDate).format(dateFormat));
    setIsCreateError(false);
  }, [curDate]);

  if (error) return <SectionErrorNotification />;
  if (!availabilities) return <SectionLoadingOverlay />;

  // Set colors blue/green for days in calendar that are selected/have availabilities
  const getDayProps: DatePickerProps['getDayProps'] = date => {
    const formattedDate = dayjs(date).format(dateFormat);
    const hasAvailability = availabilities.content
      .map(a => a.date)
      .includes(formattedDate);
    const isSelected =
      dayjs(date).format(dateFormat) === dayjs(curDate).format(dateFormat);

    if (hasAvailability) {
      if (isSelected) {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-blue-filled)',
            color: 'var(--mantine-color-white)',
          },
        };
      } else {
        return {
          style: {
            backgroundColor: 'var(--mantine-color-green-filled)',
            color: 'var(--mantine-color-white)',
          },
        };
      }
    }

    return {};
  };

  async function handleCreate() {
    if (!curDate) {
      console.error('No date selected');
      return;
    }

    const valRes = createForm.validate();
    if (valRes.hasErrors) {
      console.error('Invalid create form values:', valRes.errors);
      return;
    }

    try {
      await createAvailability(createForm.values);
    } catch (err) {
      displayError(err);
      setIsCreateError(true);
      return;
    }

    createForm.reset();
    createForm.setFieldValue('date', dayjs(curDate).format(dateFormat));

    setIsCreateError(false);
    await mutate();
    refresh();
  }

  async function handleDelete(id: number) {
    await deleteAvailability(id);
    goodNotification('Změny byly uloženy');
    await mutate();
    refresh();
  }

  async function handleRepeat() {
    if (!availabilities || !curDate) return;

    const availabilitiesToRepeat = availabilities.content.filter(a => {
      return a.date === dayjs(curDate).format(dateFormat);
    });

    if (availabilitiesToRepeat.length === 0) {
      console.info('No availabilities to repeat');
      return;
    }

    const newAvailabilities = generateAvailabilities(
      availabilitiesToRepeat,
      repeatForm.values.count,
      repeatForm.values.freq
    );

    try {
      await updateAvailabilities(newAvailabilities);
    } catch (err) {
      displayError(err);
      setIsRepeatError(true);
      return;
    }

    setIsRepeatError(false);
    await mutate();
  }

  const formattedTitleDate = curDate?.toLocaleDateString('cs', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  if (error) return <SectionErrorNotification />;
  if (!availabilities) return <SectionLoadingOverlay />;

  const dateToShow = (date: string) => {
    return dayjs(date).format('DD.MM');
  };

  const openModal = () => {
    setIsShowOpen(true);
    setIsCreateOpen(false);
    open();
  };

  const openCreateModal = () => {
    setIsShowOpen(false);
    setIsCreateOpen(true);
  };

  const closeModal = () => {
    setIsShowOpen(false);
    setIsCreateOpen(false);
    close();
  };

  return (
    <>
      <Modal
        size="xl"
        opened={opened}
        onClose={closeModal}
        centered
        title="Dispoziční časy"
      >
        {isShowOpen && (
          <div>
            <div className="flex flex-row gap-3 mb-5">
              <div className="w-1/2 flex flex-col">
                <DateInput
                  variant="filled"
                  radius="md"
                  label="Den"
                  placeholder="note"
                  valueFormat={dateFormat}
                  value={date}
                  onChange={setDate}
                  locale="cs"
                />
              </div>
              <div className="w-1/2 flex flex-col justify-between">
                <Button
                  variant="filled"
                  color="indigo"
                  onClick={openCreateModal}
                  className="mt-6"
                >
                  Přidat nastavení
                </Button>
              </div>
            </div>
            <ScrollArea h={400}>
              <ul>
                {availabilities.content
                  .filter(a => {
                    return (
                      date === null || a.date >= dayjs(date).format(dateFormat)
                    );
                  })
                  .sort((a, b): number => {
                    if (a.date < b.date) return -1;
                    else if (a.date > b.date) return 1;
                    else {
                      if (a.startTime < b.startTime) return -1;
                      else if (a.startTime > b.startTime) return 1;
                      else return 0;
                    }
                  })
                  .map(a => (
                    <li
                      key={a.id}
                      className="flex justify-between items-center mb-2"
                    >
                      <FaRegCalendarAlt />
                      <span>{dateToShow(a.date)}</span>
                      <Button
                        variant="transparent"
                        onClick={() => {
                          handleDelete(a.id);
                        }}
                      >
                        Odstranit
                      </Button>
                    </li>
                  ))}
              </ul>
            </ScrollArea>
          </div>
        )}
        {isCreateOpen && (
          <div className="flex flex-wrap flex-col content-center items-center justify-center gap-4">
            <DatePicker
              value={curDate}
              onChange={setCurDate}
              locale="cs"
              getDayProps={getDayProps}
              allowDeselect
              size="md"
            />

            {formattedTitleDate ? (
              <div className="w-full p-3 border rounded-lg border-card">
                <div className="flex flex-row justify-between mb-3">
                  <Text fw={700} size="large">
                    {formattedTitleDate}
                  </Text>
                </div>

                <ScrollArea h={250} offsetScrollbars>
                  <div className="flex flex-col gap-2">
                    {availabilities.content
                      .filter(a => {
                        return (
                          a.date === dayjs(curDate as Date).format(dateFormat)
                        );
                      })
                      .sort((a, b): number => {
                        if (a.startTime < b.startTime) {
                          return -1;
                        }
                        if (a.startTime > b.startTime) {
                          return 1;
                        }
                        return 0;
                      })
                      .map((a, index) => (
                        <div
                          key={index}
                          className="flex flex-row items-center justify-between pb-2 border-b"
                        >
                          <div className="flex flex-row items-center gap-2 mr-2">
                            <Text>od</Text>
                            <TextInput
                              variant="filled"
                              radius="md"
                              value={a.startTime}
                              readOnly
                            />
                          </div>

                          <div className="flex flex-row items-center gap-2 mr-2">
                            <Text>do</Text>
                            <TextInput
                              variant="filled"
                              radius="md"
                              value={a.endTime}
                              readOnly
                            />
                          </div>

                          <UnstyledButton
                            onClick={() => {
                              handleDelete(a.id);
                            }}
                          >
                            <TbTrashXFilled size="2rem" />
                          </UnstyledButton>
                        </div>
                      ))}
                    <div className="flex flex-row items-center justify-between pb-2 border-b">
                      <div className="flex flex-row items-center gap-2 mr-2">
                        <TimeInput
                          label="Čas od"
                          description="začátek volného času"
                          leftSection={
                            <IconClock
                              style={{ width: rem(16), height: rem(16) }}
                              stroke={1.5}
                            />
                          }
                          value={createForm.values.startTime}
                          onChange={startTime => {
                            createForm.setFieldValue('startTime', startTime);
                          }}
                        />
                      </div>

                      <div className="flex flex-row items-center gap-2 mr-2">
                        <TimeInput
                          label="Čas do"
                          description="konec volného času"
                          leftSection={
                            <IconClock
                              style={{ width: rem(16), height: rem(16) }}
                              stroke={1.5}
                            />
                          }
                          value={createForm.values.endTime}
                          onChange={endTime => {
                            createForm.setFieldValue('endTime', endTime);
                          }}
                        />
                      </div>

                      <UnstyledButton
                        onClick={handleCreate}
                        className="mr-1 ml-1"
                      >
                        <IconPlus />
                      </UnstyledButton>
                    </div>

                    {isCreateError && (
                      <Alert
                        variant="light"
                        color="red"
                        radius="xl"
                        title="Chyba!"
                        icon={<IconInfoCircle />}
                      >
                        Byl zádan nekorektní interval.
                      </Alert>
                    )}
                  </div>
                </ScrollArea>

                <div className="mt-3 flex gap-2.5 items-center justify-between">
                  <Text>Opakovat přištích</Text>
                  <NumberInput
                    defaultValue={1}
                    min={1}
                    max={5}
                    {...repeatForm.getInputProps('count')}
                  />
                  <Select
                    data={repeatFormSchema.shape.freq.options}
                    allowDeselect={false}
                    defaultValue="týdnů"
                    {...repeatForm.getInputProps('freq')}
                    className="grow"
                  />
                  <UnstyledButton onClick={handleRepeat}>
                    <LuCopyPlus size={25} />
                  </UnstyledButton>
                </div>

                {isRepeatError && (
                  <Alert
                    variant="light"
                    color="red"
                    radius="xl"
                    title="Chyba!"
                    icon={<IconInfoCircle />}
                    className="mt-3"
                  >
                    Chyba při opakování dispozičního času. Zkontrolujte kolize.
                  </Alert>
                )}
              </div>
            ) : (
              <Text>Vyberte den</Text>
            )}
          </div>
        )}
      </Modal>
      <UnstyledButton onClick={openModal}>
        <div className="flex flex-row items-center content-around justify-between p-2.5 border rounded-lg border-card">
          <h1 className="font-semibold">Dispoziční čas</h1>
          <HiPencil size="1.5rem" className="shrink-0" />
        </div>
      </UnstyledButton>
    </>
  );
}
